import { useRef, useState } from "react";
import { Camera, Loader2, Palmtree, Gauge, FileText, Download, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Panel, Field, Chip, Empty, Plate, useFetch } from "@/components/common";
import { SecureImg } from "@/components/Photos";
import { ConfirmDialog } from "@/components/DemoCleanup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fDate, fNum, todayIso } from "@/lib/api";

const LV = { ok: "bg-emerald-50 text-emerald-700 border-emerald-200", bientot: "bg-amber-50 text-amber-800 border-amber-300", expire: "bg-rose-50 text-rose-700 border-rose-200" };

function OdoCapture({ value, onChange, userId }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", "conges");
    fd.append("captured_at", new Date().toISOString());
    if (userId) fd.append("user_id", userId);
    try {
      const { data } = await api.post("/readings/ocr", fd);
      onChange({ photo_id: data.file_id, ocr: data.detected, km: data.detected ? String(data.detected) : "", warnings: data.warnings });
      if (!data.readable) toast.error("Kilométrage illisible : reprenez la photo ou saisissez-le");
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="conges-odo">
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="conges-odo-input" />
      {value.photo_id ? (
        <div className="flex gap-3 items-start">
          <SecureImg id={value.photo_id} className="w-24 h-24 rounded-lg border" testid="conges-odo-photo" />
          <div className="flex-1 space-y-2">
            <Field label={value.ocr ? `Kilométrage lu : ${fNum(value.ocr)} km — confirmez ou corrigez` : "Kilométrage du compteur"}>
              <Input inputMode="numeric" value={value.km} onChange={(e) => onChange({ ...value, km: e.target.value })} className="h-11 text-lg font-bold" data-testid="conges-km-input" />
            </Field>
            {(value.warnings || []).map((w) => <p key={w} className="text-xs text-amber-700">{w}</p>)}
            <button type="button" onClick={() => ref.current.click()} className="text-xs underline text-slate-500" data-testid="conges-odo-retake">Reprendre la photo</button>
          </div>
        </div>
      ) : (
        <Button type="button" onClick={() => ref.current.click()} disabled={busy} className="w-full h-12 bg-[#0E2D43]" data-testid="conges-odo-btn">
          {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Camera className="w-5 h-5 mr-2" />} {busy ? "Lecture du compteur…" : "Photographier le compteur"}
        </Button>
      )}
    </div>
  );
}

function StartForm({ onDone, onCancel }) {
  const [f, setF] = useState({ planned_end: "", comment: "" });
  const [odo, setOdo] = useState({});
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/m2/conges", { planned_end: f.planned_end, comment: f.comment, mileage: Number(odo.km), photo_id: odo.photo_id, ocr_detected: odo.ocr });
      toast.success("Départ en congés enregistré");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="conges-start-form">
      <div className="font-bold text-[#0E2D43]">Départ en congés avec le véhicule — {fDate(todayIso())}</div>
      <Field label="Date de retour prévue *"><Input type="date" min={todayIso()} value={f.planned_end} onChange={(e) => setF({ ...f, planned_end: e.target.value })} className="h-11" data-testid="conges-planned-end" /></Field>
      <OdoCapture value={odo} onChange={setOdo} />
      <Field label="Commentaire (facultatif)"><Input value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid="conges-comment" /></Field>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel} data-testid="conges-cancel">Annuler</Button>
        <Button onClick={send} disabled={busy || !odo.photo_id || !odo.km || !f.planned_end} className="flex-1 bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="conges-start-submit">Enregistrer le départ</Button>
      </div>
    </div>
  );
}

function EndForm({ p, onDone, onCancel }) {
  const [odo, setOdo] = useState({});
  const [busy, setBusy] = useState(false);
  const km = Number(odo.km);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/m2/conges/${p.id}/end`, { mileage: km, photo_id: odo.photo_id, ocr_detected: odo.ocr });
      toast.success(`Retour enregistré : ${fNum(data.perso_km)} km personnels pendant les congés`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="conges-end-form">
      <div className="font-bold text-[#0E2D43]">Retour de congés — compteur au départ : {fNum(p.start_km)} km</div>
      <OdoCapture value={odo} onChange={setOdo} />
      {km > 0 && <div className={`rounded-lg p-3 text-center ${km < p.start_km ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-[#0E2D43] text-white"}`} data-testid="conges-end-preview">{km < p.start_km ? `Kilométrage inférieur au départ (${fNum(p.start_km)} km) : vérifiez la valeur` : <>KM personnels congés : <b className="text-[#F49619] text-xl">{fNum(km - p.start_km)} km</b></>}</div>}
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button onClick={send} disabled={busy || !odo.photo_id || !odo.km || km < p.start_km} className="flex-1 bg-[#0E2D43]" data-testid="conges-end-submit">Terminer mes congés</Button>
      </div>
    </div>
  );
}

export function CongesBlock() {
  const { data, reload } = useFetch("/m2/conges");
  const [mode, setMode] = useState(null);
  if (!data) return null;
  const p = data.open;
  const done = () => { setMode(null); reload(); };
  return (
    <div className="space-y-3" data-testid="conges-block">
      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/commerciaux/releve" className="h-12 rounded-xl border-2 border-[#0E2D43] text-[#0E2D43] font-semibold flex items-center justify-center gap-2" data-testid="action-releve"><Gauge className="w-5 h-5" /> Faire mon relevé vendredi/lundi</Link>
        {!p && !mode && <button onClick={() => setMode("start")} className="h-12 rounded-xl bg-[#F49619] text-[#0E2D43] font-bold flex items-center justify-center gap-2" data-testid="action-conges-start"><Palmtree className="w-5 h-5" /> Départ en congés avec le véhicule</button>}
        {p && !mode && <button onClick={() => setMode("end")} className="h-12 rounded-xl bg-[#0E2D43] text-white font-bold flex items-center justify-center gap-2" data-testid="action-conges-end"><Palmtree className="w-5 h-5" /> Terminer mes congés / relever le compteur</button>}
      </div>
      {p && <div className={`rounded-xl px-4 py-3 text-sm font-semibold border ${p.overdue ? "bg-rose-50 border-rose-300 text-rose-800" : "bg-sky-50 border-sky-200 text-sky-900"}`} data-testid="conges-open-banner">
        Véhicule actuellement déclaré en période de congés depuis le {fDate(p.start_date)} · retour prévu le {fDate(p.planned_end)}{p.overdue ? " — Relevé de retour de congés manquant" : ""}</div>}
      {mode === "start" && <StartForm onDone={done} onCancel={() => setMode(null)} />}
      {mode === "end" && p && <EndForm p={p} onDone={done} onCancel={() => setMode(null)} />}
      {data.periods.some((x) => x.status === "termine") && (
        <Panel title="Mes congés avec le véhicule" testid="my-conges">
          {data.periods.filter((x) => x.status === "termine").map((x) => <div key={x.id} className="text-sm border-b last:border-0 py-2">Du {fDate(x.start_date)} au {fDate(x.end_date)} · {fNum(x.start_km)} → {fNum(x.end_km)} km · <b>{fNum(x.perso_km)} km perso</b></div>)}
        </Panel>
      )}
    </div>
  );
}

export function MyVehicleDocs() {
  const { data } = useFetch("/m2/my-vehicle/documents");
  if (!data) return null;
  return (
    <Panel title="Documents du véhicule" testid="my-vehicle-docs">
      {!data.documents.length ? <p className="text-sm text-slate-500">Aucun document disponible pour votre véhicule.</p> : data.documents.map((d) => (
        <div key={d.id} className="py-2 border-b last:border-0" data-testid={`my-doc-${d.doc_type}`}>
          <div className="flex flex-wrap items-center gap-2 text-sm"><FileText className="w-4 h-4 text-[#0E2D43]" /><b className="text-[#0E2D43]">{d.type_label}</b>
            {d.status_label && <Chip cls={LV[d.level]} testid={`my-doc-status-${d.doc_type}`}>{d.status_label}</Chip>}{d.valid_until && <span className="text-xs text-slate-500">jusqu'au {fDate(d.valid_until)}</span>}</div>
          <div className="flex flex-wrap gap-2 mt-2">{d.file_ids.map((fid, i) => <FileLink key={fid} fid={fid} n={i + 1} />)}</div>
        </div>
      ))}
    </Panel>
  );
}

function FileLink({ fid, n }) {
  const open = async () => {
    try {
      const r = await api.get(`/files/${fid}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.download = `document-${n}.${(r.data.type.split("/")[1] || "bin").replace("jpeg", "jpg")}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return <button onClick={open} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border text-sm font-semibold hover:bg-slate-50" data-testid={`my-doc-open-${fid}`}><Download className="w-4 h-4" /> Ouvrir / télécharger {n > 1 ? n : ""}</button>;
}

function FixDialog({ p, onClose, onDone }) {
  const [f, setF] = useState({ start_date: p.start_date || "", planned_end: p.planned_end || "", end_date: p.end_date || "", start_km: p.start_km ?? "", end_km: p.end_km ?? "", reason: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const run = async () => {
    const closing = p.status === "en_cours" && f.end_date && f.end_km !== "";
    try {
      await api.patch(`/m2/conges/${p.id}`, { start_date: f.start_date, planned_end: f.planned_end, end_date: f.end_date || null, start_km: Number(f.start_km),
        end_km: f.end_km === "" ? null : Number(f.end_km), reason: f.reason, ...(closing ? { status: "termine" } : {}) });
      toast.success("Période corrigée");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <ConfirmDialog title="Corriger / clôturer la période de congés" label="Enregistrer" primary onConfirm={run} onClose={onClose} testid="conges-fix">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Début"><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="conges-fix-start" /></Field>
        <Field label="Retour prévu"><Input type="date" value={f.planned_end} onChange={set("planned_end")} data-testid="conges-fix-planned" /></Field>
        <Field label="Compteur départ"><Input inputMode="numeric" value={f.start_km} onChange={set("start_km")} data-testid="conges-fix-start-km" /></Field>
        <Field label="Date de retour"><Input type="date" value={f.end_date} onChange={set("end_date")} data-testid="conges-fix-end" /></Field>
        <Field label="Compteur retour"><Input inputMode="numeric" value={f.end_km} onChange={set("end_km")} data-testid="conges-fix-end-km" /></Field>
      </div>
      <Field label="Motif de la correction *"><Input value={f.reason} onChange={set("reason")} data-testid="conges-fix-reason" /></Field>
      <p className="text-xs text-slate-500">Renseigner la date et le compteur de retour clôture une période oubliée. Chaque correction est tracée.</p>
    </ConfirmDialog>
  );
}

function CreateDialog({ userId, onClose, onDone }) {
  const [f, setF] = useState({ start_date: "", planned_end: "", mileage: "", end_date: "", end_mileage: "", comment: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async () => {
    try {
      await api.post("/m2/conges", { user_id: userId, start_date: f.start_date, planned_end: f.planned_end, mileage: Number(f.mileage), comment: f.comment,
        end_date: f.end_date || null, end_mileage: f.end_mileage === "" ? null : Number(f.end_mileage) });
      toast.success("Période de congés créée");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <ConfirmDialog title="Créer une période de congés" label="Créer" primary onConfirm={run} onClose={onClose} testid="conges-create">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Début *"><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="conges-create-start" /></Field>
        <Field label="Retour prévu *"><Input type="date" value={f.planned_end} onChange={set("planned_end")} data-testid="conges-create-planned" /></Field>
        <Field label="Compteur départ *"><Input inputMode="numeric" value={f.mileage} onChange={set("mileage")} data-testid="conges-create-km" /></Field>
        <Field label="Date de retour"><Input type="date" value={f.end_date} onChange={set("end_date")} data-testid="conges-create-end" /></Field>
        <Field label="Compteur retour"><Input inputMode="numeric" value={f.end_mileage} onChange={set("end_mileage")} data-testid="conges-create-end-km" /></Field>
      </div>
      <Field label="Commentaire"><Input value={f.comment} onChange={set("comment")} /></Field>
    </ConfirmDialog>
  );
}

export function CongesAdmin({ userId }) {
  const { data, reload } = useFetch("/m2/conges", { user_id: userId });
  const [fix, setFix] = useState(null);
  const [create, setCreate] = useState(false);
  if (!data) return null;
  const done = () => { setFix(null); setCreate(false); reload(); };
  return (
    <div className="p-4 space-y-3" data-testid="conges-admin">
      {userId ? <Button onClick={() => setCreate(true)} className="bg-[#0E2D43]" data-testid="conges-admin-create"><Plus className="w-4 h-4 mr-1" /> Créer une période de congés</Button>
        : <p className="text-sm text-slate-500">Pour créer une période à la place d'un commercial, ouvrez sa fiche (Commerciaux &amp; techniciens) → onglet « Congés avec véhicule ».</p>}
      {!data.periods.length ? <Empty text="Aucune période de congés" /> : data.periods.map((p) => (
        <div key={p.id} className="border rounded-lg p-3 text-sm space-y-2" data-testid={`conges-row-${p.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <Chip cls={p.status === "en_cours" ? (p.overdue ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-sky-50 text-sky-700 border-sky-200") : "bg-emerald-50 text-emerald-700 border-emerald-200"}>{p.status === "en_cours" ? (p.overdue ? "Retour manquant" : "Congés en cours") : "Terminée"}</Chip>
            <Plate>{p.plate}</Plate>{!userId && <b>{p.user_name}</b>}<b>Du {fDate(p.start_date)} au {p.end_date ? fDate(p.end_date) : `… (prévu ${fDate(p.planned_end)})`}</b>
            <span>{fNum(p.start_km)} → {p.end_km != null ? fNum(p.end_km) : "…"} km</span>{p.perso_km != null && <b className="text-[#0E2D43]">= {fNum(p.perso_km)} km perso</b>}
            <button onClick={() => setFix(p)} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#0E2D43] underline" data-testid={`conges-fix-${p.id}`}><Pencil className="w-3.5 h-3.5" /> Corriger / clôturer</button>
          </div>
          <div className="flex gap-2">{[p.start_photo, p.end_photo].filter(Boolean).map((id) => <SecureImg key={id} id={id} className="w-20 h-20 rounded border" testid={`conges-photo-${id}`} />)}</div>
          {p.history?.length > 0 && <details className="text-xs text-slate-500"><summary className="cursor-pointer">Historique ({p.history.length})</summary>{p.history.map((h, i) => <div key={i}>{h.at.slice(0, 16).replace("T", " ")} · {h.by} · {h.note}{Object.keys(h.changes || {}).length ? ` · ${Object.entries(h.changes).map(([k, [a, b]]) => `${k} : ${a ?? "—"} → ${b ?? "—"}`).join(", ")}` : ""}</div>)}</details>}
        </div>
      ))}
      {fix && <FixDialog p={fix} onClose={() => setFix(null)} onDone={done} />}
      {create && <CreateDialog userId={userId} onClose={() => setCreate(false)} onDone={done} />}
    </div>
  );
}

export function CongesPage() {
  return (
    <div data-testid="conges-page">
      <div className="mb-6"><div className="text-[11px] uppercase font-bold tracking-[0.2em] text-[#E0830B]">Véhicules commerciaux</div><h1 className="font-display text-3xl font-bold text-[#0E2D43]">Congés avec véhicule</h1></div>
      <div className="bg-white border border-slate-200 rounded-xl"><CongesAdmin /></div>
    </div>
  );
}

export const UsageChip = ({ w }) => (
  <Chip cls={w.usage_type === "conges" ? "bg-sky-50 text-sky-700 border-sky-200" : w.usage_type === "weekend" ? "bg-slate-50 text-slate-600 border-slate-200" : "bg-violet-50 text-violet-700 border-violet-200"}>{w.usage_label}</Chip>
);

export const PersoTypesSub = ({ t }) => (t ? `dont week-ends ${fNum(t.weekend)} km · congés ${fNum(t.conges)} km${t.historique ? ` · historique ${fNum(t.historique)} km` : ""}${t.autre ? ` · autre ${fNum(t.autre)} km` : ""}` : "");
